import { HttpBackend, HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import {
  ITXPastMeeting,
  MeetingRegistrant,
  PastMeetingSummary,
  Project,
  PublicCommittee,
  PublicMeeting,
  PublicMeetingResponse,
  PutPastMeetingSummary,
  ZoomMeeting,
  ZoomMeetingLink,
} from 'lfx-pcc';
import { DateTime } from 'luxon';
import { TreeNode } from 'primeng/api';
import { catchError, Observable, of, tap } from 'rxjs';

import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectInfrastructureService {
  public meetings: WritableSignal<PublicMeeting[]> = signal([]);
  public pastMeetings$: Observable<PublicMeetingResponse>;
  public pastMeetings: WritableSignal<PublicMeeting[]> = signal([]);
  public project: WritableSignal<Partial<Project>> = signal({} as Project);
  public date: WritableSignal<Date> = signal(new Date());
  public projects: WritableSignal<TreeNode[]> = signal([]);
  public selectedProjects: WritableSignal<TreeNode[]> = signal([]);
  public committees: WritableSignal<PublicCommittee[]> = signal([]);
  public selectedCommittee: WritableSignal<PublicCommittee> = signal({} as PublicCommittee);
  public startDate: WritableSignal<Date> = signal(new Date());
  public endDate: WritableSignal<Date> = signal(new Date());
  public slug: WritableSignal<string> = signal('');

  private publicProjectServicesAPI = environment.apiURL + '/public/project-infrastructure-services';
  private projectServicesAPI = environment.apiURL + '/project-infrastructure-services';
  private itxService = environment.apiV2URL + '/itx-services';
  private urls = {
    zoom: `${this.publicProjectServicesAPI}/zoom`,
    meetings: `${this.projectServicesAPI}/zoom/meetings`,
    publicMeetings: `${this.publicProjectServicesAPI}/zoom/meetings`,
    publicPastMeetingsSummary: `${this.itxService}/public/past_meetings`,
    pastMeetingsSummary: `${this.itxService}/zoom/past_meetings`,
  };

  private httpClient: HttpClient = inject(HttpClient);
  private httpClient2: HttpClient = inject(HttpClient);
  private router: Router = inject(Router);
  private handler: HttpBackend = inject(HttpBackend);
  private userService: UserService = inject(UserService);

  public getMeeting(id: string, params?: HttpParams): Observable<ZoomMeeting> {
    this.skipAuthCheck();

    return this.httpClient.get<ZoomMeeting>(`${this.urls.publicMeetings}/${id}`, { params, headers: { Authorization: '' } });
  }

  public getMeetingLink(id: string, params?: HttpParams): Observable<ZoomMeetingLink> {
    this.skipAuthCheck();

    return this.httpClient.get<ZoomMeetingLink>(`${this.urls.meetings}/${id}/join_link`, { params, headers: { Authorization: '' } });
  }

  public getPublicMeetingLink(id: string, params?: HttpParams): Observable<ZoomMeetingLink> {
    this.skipAuthCheck();

    return this.httpClient.get<ZoomMeetingLink>(`${this.urls.publicMeetings}/${id}/join_link`, { params, headers: { Authorization: '' } });
  }

  public postRequestInvite(id: string, data: { name: string; email: string }, params?: HttpParams): Observable<HttpResponse<any>> {
    this.skipAuthCheck();

    return this.httpClient.post<any>(`${this.urls.publicMeetings}/${id}/request-invite`, data, { params, observe: 'response', headers: { Authorization: '' } });
  }

  public getProjectMeetings(slug: string): Observable<ZoomMeeting[]> {
    return this.httpClient.get<ZoomMeeting[]>(`${this.urls.zoom}/${slug}/meetings`);
  }

  public getPublicPastMeetings(slug: string, start: Date, end: Date): Observable<PublicMeetingResponse> {
    const params = new HttpParams({
      fromObject: {
        start_date: DateTime.fromJSDate(start).toISODate() as string,
        end_date: DateTime.fromJSDate(end).toISODate() as string,
      },
    });

    return this.httpClient.get<PublicMeetingResponse>(`${this.itxService}/public/meetings/${slug}/past`, { params }).pipe(
      tap((res) => {
        this.pastMeetings.set(res.meetings);
      })
    );
  }

  public getPublicMeetings(slug: string, selectedProjects: string[] = [], selectedCommittee: string): Observable<PublicMeetingResponse> {
    this.slug.set(slug);

    // Reset meetings
    this.meetings.set([]);
    this.project.set({} as Project);
    this.projects.set([]);
    this.committees.set([]);

    const params = new HttpParams({
      fromObject: {
        view: 'pcc',
        pageSize: '9999',
      },
    });

    return this.httpClient.get<PublicMeetingResponse>(`${this.itxService}/public/meetings/${slug}`, { params }).pipe(
      catchError((error) => {
        console.error(error);
        if (error.status === 404) {
          this.router.navigate(['/meetings/project-not-found']);
        } else {
          this.router.navigate(['/service-unavailable']);
        }
        return of({ meetings: [], project: {} as Partial<Project>, committees: [] });
      }),
      tap((res) => {
        this.meetings.set(res.meetings);
        this.project.set(res.project);
        this.committees.set(res.committees);
        this.projects.set([
          {
            key: res.project.ID,
            label: res.project.Name,
            data: res.project,
            expanded: true,
            leaf: res.project.Projects && res.project.Projects.length > 0,
            children: this.mapTreeNode(res.project.Projects || [], true),
            selectable: true,
          },
        ]);

        this.selectAllProjects(res.project);

        if (selectedProjects.length > 0) {
          this.selectedProjects.set(this.selectedProjects().filter((project) => selectedProjects.includes(project.data.Slug)));
        }

        if (selectedCommittee?.length) {
          const committee = res.committees.find((c) => c.ID === selectedCommittee);

          if (committee) {
            this.selectedCommittee.set(committee);
          }
        }
      })
    );
  }

  public getPastMeetingSummaries(id: string, params?: HttpParams): Observable<PastMeetingSummary[]> {
    return this.httpClient.get<PastMeetingSummary[]>(`${this.urls.publicPastMeetingsSummary}/${id}/summaries`, { params });
  }

  public putPastMeetingSummary(meetingId: string, id: string, data: PutPastMeetingSummary, params?: HttpParams) {
    return this.httpClient.put<any>(`${this.urls.pastMeetingsSummary}/${meetingId}/summaries/${id}`, data, {
      observe: 'response',
      params,
    });
  }

  public getPastMeetingRegistrantByUserId(meetingId: string, userId: string, params?: HttpParams): Observable<MeetingRegistrant> {
    return this.httpClient.get<MeetingRegistrant>(`${this.itxService}/zoom/meetings/${meetingId}/registrants/user/${userId}`, { params });
  }

  public getPastMeeting(id: string, params?: HttpParams): Observable<ITXPastMeeting> {
    return this.httpClient.get<ITXPastMeeting>(`${this.urls.pastMeetingsSummary}/${id}`, { params });
  }

  public setDate(date: Date) {
    this.date.set(date);
  }

  public selectAllProjects(project: Project | Partial<Project>): void {
    this.selectedProjects.set([
      {
        key: project.ID,
        label: project.Name,
        data: project,
        expanded: true,
        leaf: project.Projects && project.Projects.length > 0,
        children: this.mapTreeNode(project.Projects || [], true),
        selectable: true,
      },
      ...this.mapTreeNode(project.Projects || []),
    ]);
  }

  private mapTreeNode(projects: Project[], expanded: boolean = false): TreeNode[] {
    return projects.map((project) => {
      return {
        key: project.ID,
        label: project.Name,
        data: project,
        expanded: expanded,
        leaf: project.Projects && project.Projects.length > 0,
        children: this.mapTreeNode(project.Projects || []),
        selectable: true,
      };
    });
  }

  private skipAuthCheck(): void {
    if (this.userService.skipAuth0()) {
      this.httpClient = new HttpClient(this.handler);
    } else {
      this.httpClient = this.httpClient2;
    }
  }
}
